import { Home, Users, Search, User, NotebookPen, Menu } from "lucide-react";
import { useLocation, useNavigate } from "react-router";
import type React from "react";
import { useTenantFeatureFlag } from "@/hooks/useTenantFeatureFlag";
import { path } from "@/utils/common";
import { ROUTE_MEMO, ROUTE_MY_CUSTOMERS, ROUTE_MYPAGE } from "@/utils/const";

/**
 * PRODUCT_DEV-583: Ph1.80「新括り」下部ナビ。
 * 商談記録 / 顧客一覧(マイ顧客) / 検索 / 録音 / メニュー の5タブ。
 * 通知は本タブから外し、ヘッダーのベル(Ph180BellButton)へ移設する。
 * 「録音」は既存メモタブ(/memo)のラベル変更(id/導線は memo のまま)。
 *
 * MobileTabBar と同じ props/検索オーバーレイ挙動を踏襲し、OFF↔ON で下部ナビだけが差し替わる。
 *
 * PRODUCT_DEV-675: `scheduling` フラグ ON のテナントは**3枠目が「検索」ではなく「マイページ」**になる
 * （中山さんのプロトタイプ準拠）。日程調整・活動サマリ・日報はすべてマイページの中にあり、
 * これまでマイページへはメニューシート経由でしか行けなかったため、下部ナビに常設する。
 * 追い出した検索は商談記録(ホーム)の「閲覧 / 保存」ピル行へ移す(HomeInfoLayer)。
 * フラグ OFF のときは従来どおり「検索」タブのまま（回帰なし）。
 */
interface Ph180TabBarProps {
  onMenuClick: () => void;
  onSearchClick?: () => void;
  isSearchOpen?: boolean;
  onCloseSearch?: () => void;
}

const HOME_PATH = path("/");

const Ph180TabBar: React.FC<Ph180TabBarProps> = ({
  onMenuClick,
  onSearchClick,
  isSearchOpen,
  onCloseSearch,
}) => {
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const schedulingEnabled = useTenantFeatureFlag("scheduling");

  const onDashboard = pathname === HOME_PATH;
  const onMyCustomers = pathname.startsWith(ROUTE_MY_CUSTOMERS);
  const onMyPage = pathname.startsWith(ROUTE_MYPAGE);

  // 検索オーバーレイが開いている間に他タブをタップしたら、まず overlay を閉じる。
  const wrapWithCloseSearch = (action: () => void) => () => {
    if (isSearchOpen) onCloseSearch?.();
    action();
  };

  type Tab = {
    key: "home" | "mycustomers" | "search" | "mypage" | "memo" | "menu";
    label: string;
    icon: typeof Home;
    active: boolean;
    onClick: () => void;
  };

  // 3枠目だけがフラグで入れ替わる。他の4枠は ON/OFF で同じ位置・同じ挙動。
  const thirdTab: Tab = schedulingEnabled
    ? {
        key: "mypage",
        label: "マイページ",
        icon: User,
        active: onMyPage,
        onClick: wrapWithCloseSearch(() => navigate(ROUTE_MYPAGE)),
      }
    : {
        key: "search",
        label: "検索",
        icon: Search,
        active: !!isSearchOpen,
        onClick: () => {
          if (onDashboard) {
            if (isSearchOpen) {
              onCloseSearch?.();
            } else {
              onSearchClick?.();
            }
          } else {
            navigate(`${HOME_PATH}?showFilter=true`);
          }
        },
      };

  const tabs: Tab[] = [
    {
      key: "home",
      label: "商談記録",
      icon: Home,
      active: pathname === HOME_PATH,
      onClick: wrapWithCloseSearch(() => navigate(HOME_PATH)),
    },
    {
      key: "mycustomers",
      label: "顧客一覧",
      icon: Users,
      active: onMyCustomers,
      onClick: wrapWithCloseSearch(() => navigate(ROUTE_MY_CUSTOMERS)),
    },
    thirdTab,
    {
      key: "memo",
      label: "録音",
      icon: NotebookPen,
      active: pathname === ROUTE_MEMO,
      // PRODUCT_DEV-586:「録音」タップ時はデフォルトで議事録タブを開く。
      onClick: wrapWithCloseSearch(() =>
        navigate(`${ROUTE_MEMO}?tab=minutes`),
      ),
    },
    {
      key: "menu",
      label: "メニュー",
      icon: Menu,
      active: false,
      onClick: wrapWithCloseSearch(onMenuClick),
    },
  ];

  return (
    <nav className="fixed bottom-0 left-0 right-0 bg-white border-t border-neutral-200 pb-safe-bottom z-40">
      <div className="grid grid-cols-5 h-16">
        {tabs.map((tab) => {
          const Icon = tab.icon;
          const color = tab.active ? "text-primary" : "text-neutral-500";
          const weight = tab.active ? "font-bold" : "font-normal";
          return (
            <button
              key={tab.key}
              onClick={tab.onClick}
              className="flex flex-col items-center justify-center gap-0.5 transition-colors hover:text-neutral-700"
            >
              <span className="relative">
                <Icon size={22} className={color} strokeWidth={tab.active ? 2.25 : 2} />
              </span>
              <span className={`text-[10px] ${color} ${weight}`}>{tab.label}</span>
            </button>
          );
        })}
      </div>
    </nav>
  );
};

export default Ph180TabBar;
